import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Dialog, DialogContent, CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useMst } from '../../models/rootModel';
import { UserDiscoveryResponse } from '../../models/UserDiscoveryResponse';
import { useAnalytics } from '../../utils/analytics';

import { getColor } from '../common/UICommon';
import { StepWrapperBox, LoaderDiv } from '../styledComponent/Boxes.style';
import { MainButton } from '../styledComponent/Buttons.style';

import { BaseStep, StepProps, TitleAndDescriptionProps, StepWizardProps } from '../common/BaseStep';
import { AnswersFunnelButton } from '../common/AnswersFunnelButton';

const P = styled.p`
  &.drawerTitle {
    color: ${getColor('BLACK')};
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
  }
  &.number {
    background: linear-gradient(91.86deg, #6b65e7 0%, #9e65e7 100%);
    -webkit-background-clip: Text;
    background-clip: Text;
    -webkit-text-fill-color: transparent;
    font-size: 72px;
    font-weight: 600;
    line-height: 72px;
    margin: 36px 0 16px;
  }
  &.drawerDescription {
    color: ${getColor('BLACK_60')};
    font-size: 18px;
    font-weight: 400;
    line-height: 26px;
    width: 295px;
    margin: 0 auto 40px;
  }
`;

const PopUp = styled(Dialog)`
  text-align: center;
  .MuiDialog-root {
    margin: 0;
  }
  .MuiDialog-paper {
    border-radius: 32px;
    margin: 0;
    @media (max-width: 600px) {
      border-radius: 32px 32px 0 0;
    }
  }
  .MuiDialog-scrollPaper {
    align-items: center;
    @media (max-width: 600px) {
      align-items: flex-end;
    }
  }
`;

const PopUpContent = styled(DialogContent)`
  padding: 32px 40px;
  width: fit-content;
  @media (max-width: 600px) {
    padding: 32px 32px 16px;
    width: 100vw;
  }
`;

interface PickASegmentStepProps extends TitleAndDescriptionProps, StepWizardProps, StepProps {
  failStepId: string;
  criteriaType: string;
  displayPopUp?: boolean;
  responseMapping?: {
    [key: string]: string;
  };
  criteriasToDisplay?: string[];
}

// currently not in use
export const PickASegmentStep = observer((props: PickASegmentStepProps) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [answer, setAnswer] = useState<UserDiscoveryResponse>({ type: '', name: '', meta: null });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    nextStep,
    responseMapping,
    goToNamedStep,
    displayPopUp = false,
    criteriaType,
    isActive,
    criteriasToDisplay,
  } = props;

  const { Criteria, UserDiscoveryResponse, Step } = useMst();
  const analytics = useAnalytics();

  useEffect(() => {
    if (isActive) {
      setAnswer({ type: '', name: '', meta: null });
      return window.scrollTo(0, 0);
    }
  }, [isActive]);

  const clickedAnswer = ({ type, name }: UserDiscoveryResponse) => {
    setAnswer({ type, name, meta: null });
    setIsButtonDisabled(false);
  };

  const getNextStep = () => {
    setDrawerOpen(false);
    UserDiscoveryResponse.addUserAnswers(answer);
    const nextStepId = (responseMapping && answer && responseMapping[answer.name]) || null;
    if (!nextStepId) return nextStep();
    goToNamedStep(nextStepId);
  };

  const changeButtonFunction = () => {
    if (displayPopUp) return setDrawerOpen(true);

    analytics.track(`click segment ${criteriaType}`, {
      stepId: props.id,
      stepType: props.type,
      stepIndex: props.stepIndex,
      stepTitle: props.title,
      segment: answer?.name,
      ...UserDiscoveryResponse.utms,
      origin: Step.origin,
    });

    const discoveryObj = {
      discovery: {
        byOrigin: true,
        origin: Step.origin,
        ...UserDiscoveryResponse.userAnswersObject,
      },
      ...UserDiscoveryResponse.userBirthDayAndGender,
    };

    if (Step.isReDiscovery && props.totalSteps === props.stepIndex + 1) {
      UserDiscoveryResponse.userReDiscovery(discoveryObj);
    }

    getNextStep();
  };

  return (
    <>
      {isActive && Criteria.criterias.length > 0 ? (
        <BaseStep {...props} changeButtonFunction={changeButtonFunction} isButtonDisabled={isButtonDisabled}>
          <>
            <StepWrapperBox>
              {Criteria.getCriteriasByType(criteriaType)
                .filter((criteria) => (criteriasToDisplay ? criteriasToDisplay.includes(criteria.name) : true))
                .map((criteria, index) => (
                  <AnswersFunnelButton
                    criteria={criteria}
                    key={criteria.id}
                    isActive={criteria.id === answer?.name}
                    onClick={clickedAnswer}
                  />
                ))}
            </StepWrapperBox>

            <PopUp open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              <PopUpContent>
                <P className="drawerTitle">You are not alone</P>
                <P className="number">86%</P>
                <P className="drawerDescription">{`People joined a ${answer?.name} group with Circles`}</P>

                <MainButton onClick={getNextStep}>Continue</MainButton>
              </PopUpContent>
            </PopUp>
          </>
        </BaseStep>
      ) : (
        <LoaderDiv>
          <CircularProgress />
        </LoaderDiv>
      )}
    </>
  );
});
